import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './context/AuthContext';
import './styles/global.css';

// Layout
import BottomNav from './components/layout/BottomNav';

// Pages
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import StaffHomePage from './pages/StaffHomePage';
import BarnHub from './pages/BarnHub';
import PayrollPage from './pages/PayrollPage';
import BarnProfilePage from './pages/BarnProfilePage';
import AdminPage from './pages/AdminPage';
import BetaTestersPage from './pages/BetaTestersPage';
import FeedbackPage from './pages/FeedbackPage';
import ReportIssue from './components/ReportIssue';
import { ImpersonationBanner } from './pages/AdminPage';
import VerifyEmailPage from './pages/VerifyEmailPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import WelcomePage from './pages/WelcomePage';
import HomePage from './pages/HomePage';
import HorsesPage from './pages/HorsesPage';
import DailyHorseCheckPage from './pages/DailyHorseCheckPage';
import AddHorsePage from './pages/AddHorsePage';
import HorseDetailPage from './pages/HorseDetailPage';
import HealthPage from './pages/HealthPage';
import CalendarPage from './pages/CalendarPage';
import AddEventPage from './pages/AddEventPage';
import DocumentsPage from './pages/DocumentsPage';
import ShoppingPage from './pages/ShoppingPage';
import CommunityPage from './pages/CommunityPage';
import MarketplacePage from './pages/MarketplacePage';
import ShowsPage from './pages/ShowsPage';
import RemindersPage from './pages/RemindersPage';
import ProfilePage from './pages/ProfilePage';
import EditHorsePage from './pages/EditHorsePage';
import OrganizationsPage from './pages/OrganizationsPage';
import OrgDetailPage from './pages/OrgDetailPage';
import OrgDocumentsPage from './pages/OrgDocumentsPage';
import CompliancePage from './pages/CompliancePage';
import ContactsPage from './pages/ContactsPage';
import BreedingListPage from './pages/BreedingListPage';
import BreedingRecordPage from './pages/BreedingRecordPage';
import BreedingHistoryPage from './pages/BreedingHistoryPage';
import FeedSupplementsPage from './pages/FeedSupplementsPage';
import SuppliesPage from './pages/SuppliesPage';
import SuppliesOverviewPage from './pages/SuppliesOverviewPage';
import AssignHorsesPage from './pages/AssignHorsesPage';
import StallCardPage from './pages/StallCardPage';
import SalesCardPage from './pages/SalesCardPage';
import StudCardPage from './pages/StudCardPage';
import BulkHealthPage from './pages/BulkHealthPage';
import TrailersPage from './pages/TrailersPage';
import TrailerDetailPage from './pages/TrailerDetailPage';
import SupplementStacksPage from './pages/SupplementStacksPage';
import FeedStacksPage from './pages/FeedStacksPage';
import HayStacksPage from './pages/HayStacksPage';
import WeatherWatchPage from './pages/WeatherWatchPage';
import FarrierPage from './pages/FarrierPage';
import AppointmentsPage from './pages/AppointmentsPage';
import FarrierRespondPage from './pages/FarrierRespondPage';
import FarrierOwnerRespondPage from './pages/FarrierOwnerRespondPage';
import CalendarBookingPage from './pages/CalendarBookingPage';

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return (
    <div className="loading-screen">
      <div className="spinner"></div>
      <p>Loading...</p>
    </div>
  );
  if (!user) return <Navigate to="/welcome" replace />;
  return children;
};

const AdminRoute = ({ children }) => {
  let roles = '';
  try {
    const u = JSON.parse(localStorage.getItem('cr_user') || 'null');
    roles = u ? (Array.isArray(u.roles) ? u.roles.join(',') : String(u.roles || u.role || '')) : '';
  } catch (e) {}
  let adminRoles = '';
  try {
    const au = JSON.parse(localStorage.getItem('cr_admin_user') || 'null');
    adminRoles = au ? (Array.isArray(au.roles) ? au.roles.join(',') : String(au.roles || au.role || '')) : '';
  } catch (e) {}
  const isAdmin = roles.includes('platform_admin') || roles.split(',').includes('admin')
    || adminRoles.includes('platform_admin') || adminRoles.split(',').includes('admin');
  if (!isAdmin) {
    toast.error('This account is not an approved platform admin.');
    return <Navigate to="/" replace />;
  }
  return children;
};

const AppLayout = ({ children }) => (
  <>
    {children}
    <BottomNav />
  </>
);

const App = () => {
  return (
    <AuthProvider>
      <BrowserRouter>
        <ImpersonationBanner />
      <ReportIssue />
        <Toaster
          position="top-center"
          toastOptions={{
            duration: 3000,
            style: { fontSize: '13px', maxWidth: '320px', background: '#1a1a1a', color: '#fff' }
          }}
        />
        <Routes>
          <Route path="/welcome" element={<WelcomePage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/staff" element={<ProtectedRoute><AppLayout><BarnHub /></AppLayout></ProtectedRoute>} />
          <Route path="/staff-work" element={<ProtectedRoute><AppLayout><StaffHomePage /></AppLayout></ProtectedRoute>} />
          <Route path="/payroll" element={<ProtectedRoute><AppLayout><PayrollPage /></AppLayout></ProtectedRoute>} />
          <Route path="/barn-profile" element={<ProtectedRoute><AppLayout><BarnProfilePage /></AppLayout></ProtectedRoute>} />
          <Route path="/verify-email" element={<VerifyEmailPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/farrier/respond/:token" element={<FarrierRespondPage />} />
          <Route path="/farrier/owner/:token" element={<FarrierOwnerRespondPage />} />
          <Route path="/" element={<ProtectedRoute><AppLayout><HomePage /></AppLayout></ProtectedRoute>} />
          <Route path="/home" element={<ProtectedRoute><AppLayout><HomePage /></AppLayout></ProtectedRoute>} />
          <Route path="/horses" element={<ProtectedRoute><AppLayout><HorsesPage /></AppLayout></ProtectedRoute>} />
          <Route path="/horses/add" element={<ProtectedRoute><AddHorsePage /></ProtectedRoute>} />
          <Route path="/horses/daily-check" element={<ProtectedRoute><AppLayout><DailyHorseCheckPage /></AppLayout></ProtectedRoute>} />
          <Route path="/horses/:id/edit" element={<ProtectedRoute><EditHorsePage /></ProtectedRoute>} />
          <Route path="/horses/:id" element={<ProtectedRoute><AppLayout><HorseDetailPage /></AppLayout></ProtectedRoute>} />
          <Route path="/horses/:id/health" element={<ProtectedRoute><AppLayout><HealthPage /></AppLayout></ProtectedRoute>} />
          <Route path="/calendar" element={<ProtectedRoute><AppLayout><CalendarPage /></AppLayout></ProtectedRoute>} />
          <Route path="/calendar/schedule" element={<ProtectedRoute><AppLayout><CalendarPage /></AppLayout></ProtectedRoute>} />
          <Route path="/calendar-booking" element={<ProtectedRoute><AppLayout><CalendarBookingPage /></AppLayout></ProtectedRoute>} />
          <Route path="/calendar/add" element={<ProtectedRoute><AddEventPage /></ProtectedRoute>} />
          <Route path="/documents" element={<ProtectedRoute><AppLayout><DocumentsPage /></AppLayout></ProtectedRoute>} />
          <Route path="/shop" element={<ProtectedRoute><AppLayout><ShoppingPage /></AppLayout></ProtectedRoute>} />
          <Route path="/community" element={<ProtectedRoute><AppLayout><CommunityPage /></AppLayout></ProtectedRoute>} />
          <Route path="/marketplace" element={<ProtectedRoute><AppLayout><MarketplacePage /></AppLayout></ProtectedRoute>} />
          <Route path="/shows" element={<ProtectedRoute><AppLayout><ShowsPage /></AppLayout></ProtectedRoute>} />
          <Route path="/reminders" element={<ProtectedRoute><AppLayout><RemindersPage /></AppLayout></ProtectedRoute>} />
          <Route path="/profile" element={<ProtectedRoute><AppLayout><ProfilePage /></AppLayout></ProtectedRoute>} />
          <Route path="/organizations" element={<ProtectedRoute><AppLayout><OrganizationsPage /></AppLayout></ProtectedRoute>} />
          <Route path="/organizations/:id" element={<ProtectedRoute><OrgDetailPage /></ProtectedRoute>} />
          <Route path="/organizations/:id/documents" element={<ProtectedRoute><OrgDocumentsPage /></ProtectedRoute>} />
          <Route path="/organizations/:id/documents/:docId/compliance" element={<ProtectedRoute><CompliancePage /></ProtectedRoute>} />
          <Route path="/contacts" element={<ProtectedRoute><AppLayout><ContactsPage /></AppLayout></ProtectedRoute>} />
          <Route path="/horses/:id/supplements" element={<ProtectedRoute><FeedSupplementsPage /></ProtectedRoute>} />
          <Route path="/horses/:id/supplies" element={<ProtectedRoute><SuppliesPage /></ProtectedRoute>} />
          <Route path="/supplies" element={<ProtectedRoute><AppLayout><SuppliesOverviewPage /></AppLayout></ProtectedRoute>} />
          <Route path="/assign-horses/:orgId" element={<ProtectedRoute><AppLayout><AssignHorsesPage /></AppLayout></ProtectedRoute>} />
          <Route path="/health/bulk" element={<ProtectedRoute><AppLayout><BulkHealthPage /></AppLayout></ProtectedRoute>} />
          <Route path="/trailers" element={<ProtectedRoute><AppLayout><TrailersPage /></AppLayout></ProtectedRoute>} />
          <Route path="/trailers/:id" element={<ProtectedRoute><AppLayout><TrailerDetailPage /></AppLayout></ProtectedRoute>} />
          <Route path="/horses/:id/stall-card" element={<ProtectedRoute><StallCardPage /></ProtectedRoute>} />
          <Route path="/horses/:id/sales-card" element={<ProtectedRoute><SalesCardPage /></ProtectedRoute>} />
          <Route path="/horses/:id/stud-card" element={<ProtectedRoute><StudCardPage /></ProtectedRoute>} />
          <Route path="/supplement-stacks" element={<ProtectedRoute><AppLayout><SupplementStacksPage /></AppLayout></ProtectedRoute>} />
          <Route path="/feed-stacks" element={<ProtectedRoute><AppLayout><FeedStacksPage /></AppLayout></ProtectedRoute>} />
          <Route path="/hay-stacks" element={<ProtectedRoute><AppLayout><HayStacksPage /></AppLayout></ProtectedRoute>} />
          <Route path="/weather-watch" element={<ProtectedRoute><AppLayout><WeatherWatchPage /></AppLayout></ProtectedRoute>} />
          <Route path="/appointments" element={<ProtectedRoute><AppLayout><AppointmentsPage /></AppLayout></ProtectedRoute>} />
          <Route path="/farrier" element={<ProtectedRoute><AppLayout><FarrierPage /></AppLayout></ProtectedRoute>} />
          <Route path="/horses/:id/breeding" element={<ProtectedRoute><BreedingListPage /></ProtectedRoute>} />
          <Route path="/horses/:id/breeding/history" element={<ProtectedRoute><BreedingHistoryPage /></ProtectedRoute>} />
          <Route path="/horses/:id/breeding/:recordId" element={<ProtectedRoute><BreedingRecordPage /></ProtectedRoute>} />
          <Route path="/admin" element={<AdminRoute><ProtectedRoute><AdminPage /></ProtectedRoute></AdminRoute>} />
          <Route path="/admin/beta" element={<AdminRoute><ProtectedRoute><BetaTestersPage /></ProtectedRoute></AdminRoute>} />
          <Route path="/admin/feedback" element={<AdminRoute><ProtectedRoute><FeedbackPage /></ProtectedRoute></AdminRoute>} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
};

export default App;